import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import { getProductById, getAllProducts } from '../services/productsService';
import { ShoppingCart, X, CaretLeft, CaretRight, MagnifyingGlassPlus, MagnifyingGlassMinus } from 'phosphor-react';

// Helper function to check if a product is new (released within 30 days)
const isNewProduct = (releaseDate) => {
  if (!releaseDate) return false;
  const release = new Date(releaseDate);
  const now = new Date();
  const diffTime = Math.abs(now - release);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= 30;
};

function ProductDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [isEditingQuantity, setIsEditingQuantity] = useState(false);
  const [tempQuantity, setTempQuantity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // Scroll to top when component mounts or ID changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Reset quantity when product changes
  useEffect(() => {
    if (product) {
      setQuantity(1);
      setTempQuantity(1);
      setIsEditingQuantity(false);
    }
  }, [product]);

  // Close modal with escape key and keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isImageModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeImageModal();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePreviousImage();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNextImage();
          break;
        case '+':
        case '=':
          e.preventDefault();
          handleZoomIn();
          break;
        case '-':
          e.preventDefault();
          handleZoomOut();
          break;
        case '0':
          e.preventDefault();
          handleResetZoom();
          break;
      }
    };
    
    if (isImageModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isImageModalOpen, currentImageIndex, imageZoom]);

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const productData = await getProductById(parseInt(id));
        setProduct(productData);

        // Fetch related products (same category, excluding current product)
        const allProducts = await getAllProducts();
        const related = allProducts
          .filter(p => p.category_id === productData.category_id && p.id !== productData.id)
          .slice(0, 4);
        setRelatedProducts(related);

      } catch (err) {
        setError(t('common.error'));
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, t]);

  const handleAddToCart = () => {
    if (product) {
      const cartItem = {
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: quantity,
        category_id: product.category_id,
        image_url: product.image_url,
        use_standard_pricing: product.use_standard_pricing,
        stock_quantity: product.stock_quantity
      };
      addToCart(cartItem);
    }
  };

  const handleQuantityChange = (newQuantity) => {
    // Ensure newQuantity is a valid number
    if (isNaN(newQuantity) || newQuantity < 1) {
      setQuantity(1);
      return;
    }
    
    // If product is loaded, respect stock limits
    if (product && product.stock_quantity > 0) {
      if (newQuantity <= product.stock_quantity) {
        setQuantity(newQuantity);
      } else {
        // Show toast warning when trying to exceed stock
        setToastMessage(`Maximum ${product.stock_quantity} beschikbaar`);
        setShowToast(true);
        
        // Set quantity to max available
        setQuantity(product.stock_quantity);
        
        // Hide toast after 5 seconds
        setTimeout(() => {
          setShowToast(false);
        }, 5000);
      }
    } else {
      // If product not loaded yet, allow any positive number
      setQuantity(newQuantity);
    }
  };

  const handleQuantityClick = () => {
    if (product.stock_quantity > 0) {
      setTempQuantity(quantity);
      setIsEditingQuantity(true);
    }
  };

  const handleQuantityInputChange = (e) => {
    // Allow any input without validation - just store the raw value
    setTempQuantity(e.target.value);
  };

  const handleQuantityInputBlur = () => {
    validateAndSetQuantity();
    setIsEditingQuantity(false);
  };

  const handleQuantityInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      validateAndSetQuantity();
      setIsEditingQuantity(false);
    } else if (e.key === 'Escape') {
      setTempQuantity(quantity);
      setIsEditingQuantity(false);
    }
  };

  const openImageModal = () => {
    if (product.image_url) {
      setIsImageModalOpen(true);
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setImageZoom(1);
    setCurrentImageIndex(0);
  };

  const validateAndSetQuantity = () => {
    const numericValue = parseInt(tempQuantity);
    
    // Check if input is valid
    if (isNaN(numericValue) || numericValue < 1) {
      setTempQuantity(1);
      setQuantity(1);
      return;
    }
    
    // Check stock limits
    if (product && product.stock_quantity > 0) {
      if (numericValue > product.stock_quantity) {
        // Show toast warning
        setToastMessage(`Maximum ${product.stock_quantity} beschikbaar`);
        setShowToast(true);
        
        // Set quantity to max available
        setTempQuantity(product.stock_quantity);
        setQuantity(product.stock_quantity);
        
        // Hide toast after 5 seconds
        setTimeout(() => {
          setShowToast(false);
        }, 5000);
        return;
      }
    }
    
    // Valid quantity
    setQuantity(numericValue);
  };

  // Get all images (main + additional)
  const getAllImages = () => {
    if (!product) return [];
    const images = [];
    if (product.image_url) {
      images.push({ url: product.image_url, isMain: true });
    }
    if (product.image_urls && Array.isArray(product.image_urls)) {
      product.image_urls.forEach(url => {
        images.push({ url, isMain: false });
      });
    }
    return images;
  };

  const handlePreviousImage = () => {
    const images = getAllImages();
    if (images.length > 0) {
      setCurrentImageIndex(prev => 
        prev === 0 ? images.length - 1 : prev - 1
      );
    }
  };

  const handleNextImage = () => {
    const images = getAllImages();
    if (images.length > 0) {
      setCurrentImageIndex(prev => 
        prev === images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleResetZoom = () => {
    setImageZoom(1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ambre-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || t('product.notFound')}</p>
            <Link 
              to="/shop" 
              className="bg-ambre-600 text-white px-6 py-2 rounded-lg hover:bg-ambre-700 transition"
            >
              {t('common.back')} {t('nav.shop')}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />
      
      {/* Main Product Section */}
      <section className="flex-1 py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Mobile breadcrumb and title - shown only on mobile */}
          <div className="lg:hidden mb-6">
            {/* Breadcrumb */}
            <nav className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-ambre-600 transition">{t('nav.home')}</Link>
              <span>/</span>
              <Link to="/shop" className="hover:text-ambre-600 transition">{t('nav.shop')}</Link>
              <span>/</span>
              <span className="text-gray-900">{product.name}</span>
            </nav>

            <h1 className="text-3xl font-bold font-serif tracking-tight text-gray-900">
              {product.name}
            </h1>
          </div>

          <div className="lg:grid lg:grid-cols-2 lg:gap-x-12 lg:items-start">
            {/* Image gallery */}
            <div className="flex flex-col">
              <div 
                className="w-full h-full bg-gray-100 overflow-hidden max-w-md mx-auto cursor-pointer group relative"
                onClick={openImageModal}
              >
                {isNewProduct(product.release_date) && (
                  <div className="absolute top-3 left-3 z-10">
                    <span className="bg-green-600 text-white text-sm font-medium px-3 py-1 rounded-full shadow-lg">
                      {t('product.new')}
                    </span>
                  </div>
                )}
                {product.stock_quantity === 0 && (
                  <div className="absolute top-3 right-3 z-10">
                    <span className="bg-red-500 text-white text-sm font-medium px-3 py-1 rounded-full shadow-lg">
                      {t('product.temporarilyOutOfStock')}
                    </span>
                  </div>
                )}
                {getAllImages().length > 0 ? (
                  <img 
                    src={getAllImages()[currentImageIndex]?.url} 
                    alt={product.name}
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                    style={{
                      filter: currentImageIndex === 0 ? 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' : 'none'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {/* <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
                  <span className="text-4xl font-bold text-ambre-600">{product.category?.name || 'No Category'}</span>
                </div> */}
              </div>
              
              {/* Thumbnail navigation */}
              {getAllImages().length > 1 && (
                <div className="flex gap-2 mt-4 justify-center">
                  {getAllImages().map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-16 h-16 overflow-hidden border-2 transition-all ${
                        index === currentImageIndex 
                          ? 'border-ambre-600 ring-2 ring-ambre-200' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/placeholder-product.svg';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
              
              {getAllImages().length > 0 && (
                <p className="text-center text-sm text-gray-500 mt-2">
                  Klik om te vergroten
                </p>
              )}
            </div>

            {/* Product info */}
            <div className="mt-10 px-4 sm:px-0 sm:mt-16 lg:mt-0">
              {/* Desktop breadcrumb - hidden on mobile */}
              <nav className="hidden lg:flex items-center space-x-2 text-sm text-gray-500 mb-4">
                <Link to="/" className="hover:text-ambre-600 transition">{t('nav.home')}</Link>
                <span>/</span>
                <Link to="/shop" className="hover:text-ambre-600 transition">{t('nav.shop')}</Link>
                <span>/</span>
                <span className="text-gray-900">{product.name}</span>
              </nav>

              {/* Desktop title - hidden on mobile */}
              <h1 className="hidden lg:block text-3xl font-bold font-serif tracking-tight text-gray-900 sm:text-4xl">
                {product.name}
              </h1>

              <div className="mt-3">
                <h2 className="sr-only">{t('product.information')}</h2>
                <p className="text-2xl tracking-tight text-gray-900">€{product.price.toFixed(2)}</p>
              </div>

              {/* Product specifications - simplified format */}
              <div className="mt-6">
                <div className="text-gray-600">
                  {product.dimensions && product.material && (
                    <p>{product.dimensions} on {product.material}</p>
                  )}
                  {(!product.dimensions || !product.material) && product.dimensions && (
                    <p>{product.dimensions}</p>
                  )}
                  {(!product.dimensions || !product.material) && product.material && (
                    <p>{product.material}</p>
                  )}
                </div>
              </div>

              {/* Stock status */}
              <div className="mt-6 flex items-center">
                <div className="flex items-center">
                  <div className={`h-2 w-2 rounded-full mr-2 ${product.stock_quantity > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <span className={`text-sm font-medium ${product.stock_quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {product.stock_quantity > 0 ? t('product.inStock') : t('product.outOfStock')}
                  </span>
                </div>
              </div>

              {/* Quantity and Add to cart */}
              <div className="mt-10">
                {/* Quantity selector */}
                <div className="mb-6">
                  <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('product.quantity')}
                  </label>
                  <div className="flex items-center border border-gray-300 rounded-lg w-fit">
                    <button
                      onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
                      disabled={product.stock_quantity === 0 || quantity <= 1}
                      className="p-3 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      -
                    </button>
                    {isEditingQuantity ? (
                      <input
                        type="number"
                        value={tempQuantity}
                        onChange={handleQuantityInputChange}
                        onBlur={handleQuantityInputBlur}
                        onKeyDown={handleQuantityInputKeyDown}
                        min="1"
                        max={product.stock_quantity}
                        className="px-4 py-3 text-center min-w-12 text-lg font-medium border-none outline-none focus:ring-2 focus:ring-ambre-500"
                        autoFocus
                      />
                    ) : (
                      <span 
                        className="px-4 py-3 text-center min-w-12 text-lg font-medium cursor-pointer hover:bg-gray-50 transition"
                        onClick={handleQuantityClick}
                        title="Click to edit quantity"
                      >
                        {quantity}
                      </span>
                    )}
                    <button
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={product.stock_quantity === 0}
                      className="p-3 hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                  {product.stock_quantity > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {t('product.maxQuantity', { max: product.stock_quantity })}
                    </p>
                  )}
                  
                  {/* Toast Notification - positioned under quantity input */}
                  {showToast && (
                    <div className="mt-2 animate-in slide-in-from-top duration-300 max-w-xs">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm text-red-500 font-bold">{toastMessage}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Add to cart button */}
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock_quantity === 0}
                  className={`w-full border border-transparent rounded-md py-3 px-8 flex items-center justify-center text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    product.stock_quantity > 0
                      ? 'bg-ambre-600 hover:bg-ambre-700 focus:ring-ambre-500'
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                >
                  <ShoppingCart size={20} className="mr-2" />
                  {t('product.addToCart')}
                </button>
              </div>

              {/* Product description */}
              {product.description && (
                <div className="mt-10">
                  <h3 className="text-sm font-medium text-gray-900">{t('product.description')}</h3>
                  <div className="mt-4 prose prose-sm text-gray-500">
                    <p>{product.description}</p>
                  </div>
                </div>
              )}

              {/* Additional specifications */}
              <div className="mt-8">
                <h3 className="text-sm font-medium text-gray-900">{t('product.details')}</h3>
                <div className="mt-4 space-y-2">
                  <ul>
                    <li>
                      <span className="text-gray-500">Ongevouwen kaart met blanco achterzijde, A6-formaat (105 x 148 mm)</span>
                    </li>
                    <li>
                      <span className="text-gray-500">Gedrukt op kwaliteitsvol papier met een subtiele lijnstructuur (300 gram/m²)</span>
                    </li>
                    <li>
                      <span className="text-gray-500">Inclusief bruine envelop gemaakt van gerecycleerd papier (C6-formaat)</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-sm font-medium text-gray-900">Verzendkosten</h3>
                <div className="mt-4 space-y-2">
                  <ul>
                    <li>
                      <span className="text-gray-500">De verzendkosten bedragen €3.49. (Vanaf een bedrag van €30.00 is de verzending gratis.)</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8">
                <h3 className="text-sm font-medium text-gray-900">Leveringstermijn</h3>
                <div className="mt-4 space-y-2">
                  <ul>
                    <li>
                      <span className="text-gray-500">De leveringstermijn is 4 tot 6 werkdagen.</span>
                    </li>
                  </ul>
                </div>
              </div>
              {/* {(product.finish || product.weight || product.origin || product.sku) && (
                <div className="mt-8">
                  <h3 className="text-sm font-medium text-gray-900">{t('product.details')}</h3>
                  <div className="mt-4 space-y-2">
                    {product.finish && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('product.finish')}:</span>
                        <span className="text-gray-900">{product.finish}</span>
                      </div>
                    )}
                    {product.weight && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('product.weight')}:</span>
                        <span className="text-gray-900">{product.weight}</span>
                      </div>
                    )}
                    {product.origin && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('product.origin')}:</span>
                        <span className="text-gray-900">{product.origin}</span>
                      </div>
                    )}
                    {product.sku && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">{t('product.sku')}:</span>
                        <span className="text-gray-900">{product.sku}</span>
                      </div>
                    )}
                  </div>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900 mb-8">Misschien vind je deze ook leuk?</h2>
            <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-6 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8">
              {relatedProducts.map((relatedProduct) => (
                <Link key={relatedProduct.id} to={`/product/${relatedProduct.id}`} className="group relative">
                  <div className="w-full bg-gray-200 overflow-hidden group-hover:opacity-75 transition-opacity shadow-lg relative" style={{ aspectRatio: '105/148' }}>
                    {isNewProduct(relatedProduct.release_date) && (
                      <div className="absolute top-2 left-2 z-10">
                        <span className="bg-green-600 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                          {t('product.new')}
                        </span>
                      </div>
                    )}
                    {relatedProduct.stock_quantity === 0 && (
                      <div className="absolute top-2 right-2 z-10">
                        <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded-full shadow-md">
                          {t('product.temporarilyOutOfStock')}
                        </span>
                      </div>
                    )}
                    {relatedProduct.image_url ? (
                      <img 
                        src={relatedProduct.image_url} 
                        alt={relatedProduct.name}
                        className="w-full h-full object-center object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full bg-gradient-to-br from-ambre-100 to-ambre-200 flex items-center justify-center">
                      <span className="text-lg font-bold text-ambre-600">{relatedProduct.category?.name || 'No Category'}</span>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 flex-1">
                        <span aria-hidden="true" className="absolute inset-0" />
                        {relatedProduct.name}
                      </h3>
                      <span className="ml-3 px-2 py-1 text-xs font-medium bg-ambre-100 text-ambre-800 rounded-full">
                        {relatedProduct.category?.name || 'No Category'}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-ambre-600">€{relatedProduct.price.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Image Gallery Modal */}
      {isImageModalOpen && getAllImages().length > 0 && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div className="relative w-full h-full flex flex-col items-center justify-center max-w-full max-h-full">
            {/* Close button */}
            <button
              onClick={closeImageModal}
              className="absolute top-4 right-4 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all z-10"
            >
              <X size={24} className="text-white" />
            </button>

            {/* Main image display */}
            <div className="relative flex-1 flex items-center justify-center w-full h-full overflow-hidden">
              <img
                src={getAllImages()[currentImageIndex]?.url}
                alt={product.name}
                className="object-contain transition-transform duration-200"
                style={{ 
                  transform: `scale(${imageZoom})`,
                  maxWidth: 'calc(100vw - 8rem)',
                  maxHeight: 'calc(100vh - 8rem)',
                  width: 'auto',
                  height: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  e.target.src = '/placeholder-product.svg';
                }}
              />

              {/* Navigation arrows */}
              {getAllImages().length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePreviousImage();
                    }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-3 transition-all"
                  >
                    <CaretLeft size={24} className="text-white" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNextImage();
                    }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-3 transition-all"
                  >
                    <CaretRight size={24} className="text-white" />
                  </button>
                </>
              )}

              {/* Zoom controls */}
              <div className="absolute top-4 left-4 flex gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomOut();
                  }}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all"
                >
                  <MagnifyingGlassMinus size={20} className="text-white" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleResetZoom();
                  }}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full px-3 py-2 transition-all text-sm text-white"
                >
                  {Math.round(imageZoom * 100)}%
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleZoomIn();
                  }}
                  className="bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full p-2 transition-all"
                >
                  <MagnifyingGlassPlus size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Image counter and thumbnails */}
            {getAllImages().length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-4">
                {/* Image counter */}
                <div className="bg-white bg-opacity-20 rounded-full px-4 py-2 text-white text-sm">
                  {currentImageIndex + 1} / {getAllImages().length}
                </div>

                {/* Thumbnail strip */}
                <div className="flex gap-2">
                  {getAllImages().map((image, index) => (
                    <button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleThumbnailClick(index);
                      }}
                      className={`w-12 h-12 overflow-hidden border-2 transition-all ${
                        index === currentImageIndex 
                          ? 'border-white' 
                          : 'border-white border-opacity-50 hover:border-opacity-75'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/placeholder-product.svg';
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Keyboard shortcuts help */}
            <div className="absolute bottom-4 right-4 bg-white bg-opacity-20 rounded-lg px-3 py-2 text-white text-xs">
              <div>← → Navigate</div>
              <div>+ - Zoom</div>
              <div>0 Reset</div>
              <div>Esc Close</div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
}

export default ProductDetails; 